import { useEffect, useState } from 'react'
import { dashboardClient } from '@/api'
import { useParams } from 'react-router-dom'
import { Badge, IFrame } from '@/ui'
import { getStatusIcon } from '../components/PaymentStatusChip'
import { PaymentRead, PaymentStatus } from '@/api/dashboard'
import { cn } from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { CenteredProgress } from '@/components'
import { AnimatePresence, motion, useAnimation } from 'framer-motion'

export const ReceiptEmailView = (props: { className?: string; payment: PaymentRead | undefined }) => {
  const { payment } = props
  const { companyId, gameId } = useParams() as {
    companyId: string
    gameId: string
  }
  const { t } = useTranslation()
  const [isError, setIsError] = useState(false)
  const [html, setHtml] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isPaymentLoading, setIsPaymentLoading] = useState(false)
  const availableStatuses = [
    PaymentStatus.Done,
    PaymentStatus.Refunded,
    PaymentStatus.RefundRequested,
    PaymentStatus.Canceled,
  ]

  const zoom = 0.9
  const width = 600 * zoom
  const controls = useAnimation()

  const loadEmail = async () => {
    try {
      if (!payment) {
        return
      }
      if (availableStatuses.includes(payment.status as PaymentStatus)) {
        let html = ''
        try {
          const { data } = await dashboardClient.v1.getReceiptEmail(companyId, gameId, payment.order_id!)
          html = data.html
        } catch (e) {
          setIsError(true)
        }

        if (!html) {
          return
        }

        const removeLinks =
          "let links = document.querySelectorAll('a'); \n" + "for (let link of links) {link.removeAttribute('href')}"

        setHtml(
          html +
            `<style>html {zoom: ${zoom}}</style>` +
            `<style>
          @media only screen and (max-device-width: 599px), only screen and (max-width: 599px) {
          .stack-column-center {
              display: table-cell !important;
          }
         </style>` +
            `<script>
              ${removeLinks}
         </script>`,
        )
      }
    } finally {
      setTimeout(() => setIsLoading(false), 1000)
    }
  }

  useEffect(() => {
    if (payment) {
      loadEmail()
      setIsPaymentLoading(false)
    }
  }, [payment?.order_id, payment?.status])

  const renderUnavailable = () => {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center  overflow-x-hidden rounded-xl border-2 border-border-secondary bg-fg-secondary-hover',
          props.className,
        )}
      >
        <svg width="400" height="8" viewBox="0 0 400 8" fill="none" className="relative">
          <path
            d="M0.5 4.89637L0.5 7.99999L0.5 8.5L399.5 8.5L399.5 7.99998L399.5 4.89637C399.5 2.85236 397.18 1.67217 395.528 2.87555L391.417 5.87019C390.256 6.71559 388.696 6.76623 387.483 5.99791L380.285 1.43901C379.468 0.921747 378.427 0.921747 377.61 1.43902L370.294 6.07247C369.15 6.79665 367.692 6.79665 366.548 6.07247L359.232 1.43902C358.416 0.921749 357.374 0.921747 356.557 1.43902L349.241 6.07247C348.098 6.79664 346.639 6.79664 345.496 6.07247L338.18 1.43901C337.363 0.921747 336.321 0.921746 335.504 1.43902L328.188 6.07247C327.045 6.79664 325.587 6.79665 324.443 6.07247L317.127 1.43902C316.31 0.921748 315.269 0.921748 314.452 1.43902L307.136 6.07247C305.992 6.79665 304.534 6.79665 303.39 6.07248L296.074 1.43902C295.258 0.92175 294.216 0.921748 293.399 1.43902L286.083 6.07247C284.94 6.79665 283.481 6.79664 282.338 6.07248L275.022 1.43901C274.205 0.921747 273.163 0.921747 272.347 1.43902L265.031 6.07247C263.887 6.79665 262.429 6.79665 261.285 6.07247L253.969 1.43902C253.152 0.921749 252.111 0.921749 251.294 1.43902L243.978 6.07247C242.835 6.79665 241.376 6.79665 240.233 6.07247L232.917 1.43902C232.1 0.92175 231.058 0.92175 230.241 1.43902L222.925 6.07247C221.782 6.79665 220.323 6.79665 219.18 6.07247L211.864 1.43902C211.047 0.92175 210.005 0.921749 209.189 1.43902L201.873 6.07247C200.729 6.79665 199.271 6.79665 198.127 6.07247L190.811 1.43902C189.995 0.921751 188.953 0.92175 188.136 1.43902L180.82 6.07247C179.677 6.79665 178.218 6.79665 177.075 6.07248L169.759 1.43902C168.942 0.921751 167.9 0.92175 167.083 1.43902L159.767 6.07247C158.624 6.79665 157.166 6.79665 156.022 6.07247L148.706 1.43902C147.889 0.92175 146.848 0.92175 146.031 1.43902L138.715 6.07247C137.571 6.79665 136.113 6.79665 134.969 6.07247L127.653 1.43902C126.837 0.92175 125.795 0.921751 124.978 1.43902L117.662 6.07247C116.519 6.79665 115.06 6.79665 113.917 6.07247L106.601 1.43902C105.784 0.921752 104.742 0.921752 103.926 1.43902L96.6095 6.07248C95.4661 6.79665 94.0076 6.79665 92.8642 6.07248L85.5481 1.43902C84.7314 0.921751 83.6896 0.921751 82.8729 1.43902L75.5569 6.07247C74.4135 6.79665 72.955 6.79665 71.8115 6.07248L64.4955 1.43902C63.6788 0.921752 62.637 0.921752 61.8203 1.43902L54.5043 6.07247C53.3608 6.79665 51.9023 6.79665 50.7589 6.07248L43.4429 1.43902C42.6262 0.921753 41.5844 0.921752 40.7676 1.43902L33.4516 6.07248C32.3082 6.79665 30.8497 6.79665 29.7063 6.07248L22.3903 1.43902C21.5735 0.921752 20.5317 0.921752 19.715 1.43902L12.5167 5.99791C11.3036 6.76623 9.74422 6.7156 8.58348 5.8702L4.47183 2.87555C2.8196 1.67218 0.5 2.85236 0.5 4.89637Z"
            stroke="#E2E8F0"
            fill="#F8FAFC"
          />
        </svg>
        <div
          style={{
            boxShadow: '0px 12px 16px -4px #10182814',
          }}
          className="flex h-[515px] w-[400px] flex-col items-center justify-center border-x border-border-primary bg-fg-primary-hover text-caption-md text-text-tertiary"
        >
          <Badge className="px-1" variant="gray-primary">
            {payment && getStatusIcon([{ status: payment.status }])}
          </Badge>
          <span className="mt-[18px] text-caption-md font-semibold text-text-secondary">
            {payment && t(`transactions-table.status.${payment.status}`)}
          </span>
          <span className="mt-1.5 text-caption-sm text-text-tertiary">
            {t('transaction-details.receipt-unavailable')}
          </span>
        </div>
        <svg width="400" height="8" viewBox="0 0 400 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0.5 3.10363L0.5 1.33733e-05L0.5 -0.5L399.5 -0.5L399.5 1.74627e-05L399.5 3.10363C399.5 5.14764 397.18 6.32783 395.528 5.12445L391.417 2.12981C390.256 1.28441 388.696 1.23377 387.483 2.00209L380.285 6.56099C379.468 7.07825 378.427 7.07825 377.61 6.56098L370.294 1.92753C369.15 1.20335 367.692 1.20335 366.548 1.92753L359.232 6.56098C358.416 7.07825 357.374 7.07825 356.557 6.56098L349.241 1.92753C348.098 1.20336 346.639 1.20336 345.496 1.92753L338.18 6.56099C337.363 7.07825 336.321 7.07825 335.504 6.56098L328.188 1.92753C327.045 1.20336 325.587 1.20335 324.443 1.92753L317.127 6.56098C316.31 7.07825 315.269 7.07825 314.452 6.56098L307.136 1.92753C305.992 1.20335 304.534 1.20335 303.39 1.92752L296.074 6.56098C295.258 7.07825 294.216 7.07825 293.399 6.56098L286.083 1.92753C284.94 1.20335 283.481 1.20336 282.338 1.92752L275.022 6.56099C274.205 7.07825 273.163 7.07825 272.347 6.56098L265.031 1.92753C263.887 1.20335 262.429 1.20335 261.285 1.92753L253.969 6.56098C253.152 7.07825 252.111 7.07825 251.294 6.56098L243.978 1.92753C242.835 1.20335 241.376 1.20335 240.233 1.92753L232.917 6.56098C232.1 7.07825 231.058 7.07825 230.241 6.56098L222.925 1.92753C221.782 1.20335 220.323 1.20335 219.18 1.92753L211.864 6.56098C211.047 7.07825 210.005 7.07825 209.189 6.56098L201.873 1.92753C200.729 1.20335 199.271 1.20335 198.127 1.92753L190.811 6.56098C189.995 7.07825 188.953 7.07825 188.136 6.56098L180.82 1.92753C179.677 1.20335 178.218 1.20335 177.075 1.92752L169.759 6.56098C168.942 7.07825 167.9 7.07825 167.083 6.56098L159.767 1.92753C158.624 1.20335 157.166 1.20335 156.022 1.92753L148.706 6.56098C147.889 7.07825 146.848 7.07825 146.031 6.56098L138.715 1.92753C137.571 1.20335 136.113 1.20335 134.969 1.92753L127.653 6.56098C126.837 7.07825 125.795 7.07825 124.978 6.56098L117.662 1.92753C116.519 1.20335 115.06 1.20335 113.917 1.92753L106.601 6.56098C105.784 7.07825 104.742 7.07825 103.926 6.56098L96.6095 1.92752C95.4661 1.20335 94.0076 1.20335 92.8642 1.92752L85.5481 6.56098C84.7314 7.07825 83.6896 7.07825 82.8729 6.56098L75.5569 1.92753C74.4135 1.20335 72.955 1.20335 71.8115 1.92752L64.4955 6.56098C63.6788 7.07825 62.637 7.07825 61.8203 6.56098L54.5043 1.92753C53.3608 1.20335 51.9023 1.20335 50.7589 1.92752L43.4429 6.56098C42.6262 7.07825 41.5844 7.07825 40.7676 6.56098L33.4516 1.92752C32.3082 1.20335 30.8497 1.20335 29.7063 1.92752L22.3903 6.56098C21.5735 7.07825 20.5317 7.07825 19.715 6.56098L12.5167 2.00209C11.3036 1.23377 9.74422 1.2844 8.58348 2.1298L4.47183 5.12445C2.8196 6.32782 0.5 5.14764 0.5 3.10363Z"
            stroke="#E2E8F0"
            fill="#F8FAFC"
          />
        </svg>
      </div>
    )
  }

  const isAvailable = !isError && payment && availableStatuses.includes(payment.status as PaymentStatus)

  if (isLoading || isPaymentLoading) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center  overflow-x-hidden rounded-xl border-2 border-border-secondary',
          props.className,
        )}
      >
        <CenteredProgress />
      </div>
    )
  }

  const renderSeal = () => {
    let svgUrl: string = ''

    switch (payment?.status) {
      case PaymentStatus.Canceled:
        svgUrl = '/icons/receipt-canceled.svg'
        break
      case PaymentStatus.Refunded:
        svgUrl = '/icons/receipt-refunded.svg'
        break
      default:
        return null
    }
    return <img src={svgUrl} alt="seal" className="absolute bottom-[40%] right-[19%] size-[124px]" />
  }

  const onFrameLoad = () => {
    controls.start('visible')
  }

  if (isAvailable) {
    return (
      <div
        className={cn(
          'ph-no-capture flex flex-col items-center justify-center overflow-x-hidden rounded-xl border-2 border-border-secondary bg-fg-secondary-hover',
          props.className,
        )}
      >
        <AnimatePresence mode="wait">
          <div className="my-[35px] h-auto" style={{ width: width + 'px' }}>
            {html && (
              <motion.div
                animate={controls}
                initial="hidden"
                exit="hidden"
                variants={{
                  visible: { opacity: 1 },
                  hidden: { opacity: 0 },
                }}
              >
                <svg width={width} height="12" viewBox="0 0 600 12" fill="none" className="relative top-1">
                  <path
                    d="M-3.32364e-07 4.39639L0 12L600 12L600 4.39638C600 1.94356 597.216 0.527346 595.234 1.97139L585.859 8.79935C584.864 9.52398 583.527 9.56739 582.488 8.90883L570.026 1.01661C569.046 0.395895 567.796 0.395896 566.816 1.01661L554.237 8.98339C553.257 9.60411 552.007 9.60411 551.026 8.98339L538.447 1.01662C537.467 0.395896 536.217 0.395894 535.237 1.01662L522.658 8.98339C521.678 9.60411 520.428 9.60411 519.448 8.98339L506.868 1.01661C505.888 0.395894 504.638 0.395894 503.658 1.01661L491.079 8.98339C490.099 9.60411 488.849 9.60411 487.869 8.98339L475.289 1.01662C474.309 0.395896 473.059 0.395896 472.079 1.01662L459.5 8.98339C458.52 9.60411 457.27 9.60411 456.29 8.98339L443.71 1.01662C442.73 0.395898 441.48 0.395896 440.5 1.01662L427.921 8.98339C426.941 9.60411 425.691 9.60411 424.711 8.98339L412.131 1.01662C411.151 0.395896 409.901 0.395896 408.921 1.01662L396.342 8.98339C395.362 9.60411 394.112 9.60411 393.132 8.98339L380.553 1.01662C379.572 0.395897 378.322 0.395896 377.342 1.01662L364.763 8.98339C363.783 9.60411 362.533 9.60411 361.553 8.98339L348.974 1.01662C347.993 0.395897 346.743 0.395898 345.763 1.01662L333.184 8.98339C332.204 9.60411 330.954 9.60411 329.974 8.98339L317.395 1.01662C316.415 0.395898 315.164 0.395897 314.184 1.01662L301.605 8.98339C300.625 9.60411 299.375 9.60411 298.395 8.98339L285.816 1.01662C284.836 0.395899 283.585 0.395898 282.605 1.01662L270.026 8.98339C269.046 9.60411 267.796 9.60411 266.816 8.98339L254.237 1.01662C253.257 0.395899 252.007 0.395897 251.026 1.01662L238.447 8.98339C237.467 9.60411 236.217 9.60411 235.237 8.98339L222.658 1.01662C221.678 0.395899 220.428 0.395898 219.447 1.01662L206.868 8.98339C205.888 9.60411 204.638 9.60411 203.658 8.98339L191.079 1.01662C190.099 0.395899 188.849 0.395899 187.869 1.01662L175.289 8.98339C174.309 9.60411 173.059 9.60412 172.079 8.9834L159.5 1.01662C158.52 0.3959 157.27 0.3959 156.29 1.01662L143.71 8.9834C142.73 9.60411 141.48 9.60412 140.5 8.9834L127.921 1.01662C126.941 0.3959 125.691 0.3959 124.711 1.01662L112.131 8.9834C111.151 9.60411 109.901 9.60412 108.921 8.9834L96.342 1.01662C95.3619 0.3959 94.1118 0.3959 93.1317 1.01662L80.5525 8.9834C79.5724 9.60412 78.3223 9.60412 77.3422 8.9834L64.763 1.01662C63.783 0.395902 62.5328 0.395901 61.5527 1.01662L48.9736 8.9834C47.9935 9.60412 46.7434 9.60412 45.7633 8.9834L33.1841 1.01662C32.204 0.395901 30.9539 0.395901 29.9738 1.01662L17.5124 8.90883C16.4725 9.56739 15.1359 9.52399 14.141 8.79936L4.76619 1.97139C2.78351 0.527348 -4.3958e-07 1.94357 -3.32364e-07 4.39639Z"
                    fill="white"
                  />
                </svg>
                <div className="h-[30px] bg-fg-primary" />
                <div className="relative">
                  <IFrame
                    html={html}
                    zoom={zoom}
                    onLoad={onFrameLoad}
                    style={{
                      boxShadow: '0px 12px 16px -4px #10182814, 0px 4px 6px -2px #10182808',
                      width: width + 'px',
                    }}
                  />
                  {renderSeal()}
                </div>
                <svg
                  width={width}
                  height="12"
                  viewBox="0 0 600 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="relative -top-px"
                >
                  <path
                    d="M-3.32364e-07 7.60361L0 1.99323e-05L600 2.62268e-05L600 7.60362C600 10.0564 597.216 11.4727 595.234 10.0286L585.859 3.20065C584.864 2.47602 583.527 2.43261 582.488 3.09117L570.026 10.9834C569.046 11.6041 567.796 11.6041 566.816 10.9834L554.237 3.01661C553.257 2.39589 552.007 2.39589 551.026 3.01661L538.447 10.9834C537.467 11.6041 536.217 11.6041 535.237 10.9834L522.658 3.01661C521.678 2.39589 520.428 2.39589 519.448 3.01661L506.868 10.9834C505.888 11.6041 504.638 11.6041 503.658 10.9834L491.079 3.01661C490.099 2.39589 488.849 2.39589 487.869 3.01661L475.289 10.9834C474.309 11.6041 473.059 11.6041 472.079 10.9834L459.5 3.01661C458.52 2.39589 457.27 2.39589 456.29 3.01661L443.71 10.9834C442.73 11.6041 441.48 11.6041 440.5 10.9834L427.921 3.01661C426.941 2.39589 425.691 2.39589 424.711 3.01661L412.131 10.9834C411.151 11.6041 409.901 11.6041 408.921 10.9834L396.342 3.01661C395.362 2.39589 394.112 2.39589 393.132 3.01661L380.553 10.9834C379.572 11.6041 378.322 11.6041 377.342 10.9834L364.763 3.01661C363.783 2.39589 362.533 2.39589 361.553 3.01661L348.974 10.9834C347.993 11.6041 346.743 11.6041 345.763 10.9834L333.184 3.01661C332.204 2.39589 330.954 2.39589 329.974 3.01661L317.395 10.9834C316.415 11.6041 315.164 11.6041 314.184 10.9834L301.605 3.01661C300.625 2.39589 299.375 2.39589 298.395 3.01661L285.816 10.9834C284.836 11.6041 283.585 11.6041 282.605 10.9834L270.026 3.01661C269.046 2.39589 267.796 2.39589 266.816 3.01661L254.237 10.9834C253.257 11.6041 252.007 11.6041 251.026 10.9834L238.447 3.01661C237.467 2.39589 236.217 2.39589 235.237 3.01661L222.658 10.9834C221.678 11.6041 220.428 11.6041 219.447 10.9834L206.868 3.01661C205.888 2.39589 204.638 2.39589 203.658 3.01661L191.079 10.9834C190.099 11.6041 188.849 11.6041 187.869 10.9834L175.289 3.01661C174.309 2.39589 173.059 2.39588 172.079 3.0166L159.5 10.9834C158.52 11.6041 157.27 11.6041 156.29 10.9834L143.71 3.0166C142.73 2.39589 141.48 2.39588 140.5 3.0166L127.921 10.9834C126.941 11.6041 125.691 11.6041 124.711 10.9834L112.131 3.0166C111.151 2.39589 109.901 2.39588 108.921 3.0166L96.342 10.9834C95.3619 11.6041 94.1118 11.6041 93.1317 10.9834L80.5525 3.0166C79.5724 2.39588 78.3223 2.39588 77.3422 3.0166L64.763 10.9834C63.783 11.6041 62.5328 11.6041 61.5527 10.9834L48.9736 3.0166C47.9935 2.39588 46.7434 2.39588 45.7633 3.0166L33.1841 10.9834C32.204 11.6041 30.9539 11.6041 29.9738 10.9834L17.5124 3.09117C16.4725 2.43261 15.1359 2.47601 14.141 3.20064L4.76619 10.0286C2.78351 11.4727 -4.3958e-07 10.0564 -3.32364e-07 7.60361Z"
                    fill="white"
                  />
                </svg>
              </motion.div>
            )}
          </div>
        </AnimatePresence>
      </div>
    )
  }

  return renderUnavailable()
}
